import { Base64File, ImageMimeType, ImageRatio, ImageType } from '@synergy/models';
import { Box, FormHelperText, Grid } from '@mui/material';
import { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { Field, FieldInputProps, FieldProps, Formik, FormikProps, isNaN, useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    FormikPoolDatePicker,
    PoolFormattedMessage,
    PoolSpinner,
    PoolTextField,
    PoolTypography,
    ScrollToFieldError,
} from '@synergy/components';
import { MAX_FILE_SIZE } from '@synergy/config';
import { PaloFormSection } from '@synergy/components-palo';
import { base64encode, getLocalizedString } from '@synergy/utils';
import { useNavigate, useParams } from 'react-router';

import * as HeaderBannerActions from '../store/header-banner-actions';
import * as HeaderBannerDetailActions from './store/header-banner-detail-actions';
import { HeaderBannerFormValues } from './models/header-banner-form-values';
import { OptionalSectionTitle } from '../../components/optional-section-title';
import { PaloContentDetailShell } from '../../palo/palo-content-detail-shell';
import { PictureDropzone } from '../../components/picture-dropzone';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { getValidationSchema } from './header-banner-detail-validation-schema';
import {
    headerBannerDetailFormValues,
    headerBannerDetailRequestingSelector,
    headerBannerDetailSelector,
} from './store/header-banner-detail-selectors';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const validImageTypes = [ImageType.JPEG, ImageType.PNG, ImageType.WebP];
const validImageMimeTypes = validImageTypes.map(type => ImageMimeType[type].toString());

export const HeaderBannerDetail = () => {
    const requesting = useAppSelector(headerBannerDetailRequestingSelector);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const headerBannerFormValues = useAppSelector(headerBannerDetailFormValues);
    const headerBanner = useAppSelector(headerBannerDetailSelector);
    const dispatch = useThunkDispatch();
    const [newImageFile, setNewImageFile] = useState<Base64File | undefined>(undefined);
    const [newMobileImageFile, setNewMobileImageFile] = useState<Base64File | undefined>(undefined);
    const { id: headerBannerIdParam } = useParams<{ id: string }>();
    const intl = useIntl();
    const navigate = useNavigate();

    useEffect(() => {
        if (headerBannerIdParam && !isNaN(+headerBannerIdParam)) {
            dispatch(HeaderBannerDetailActions.fetchHeaderBannerDetail(+headerBannerIdParam));
        }

        return () => {
            dispatch(HeaderBannerDetailActions.resetHeaderBannerDetail());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isNaN(Number(headerBannerIdParam)) && headerBanner?.id) {
            navigate(ROUTES.contentManagement.headerBanner.detail.replace(':id', headerBanner.id.toString()), {
                replace: true,
            });
        }
        // ignore headerBannerIdParam, should only rerun if headerBanner.id changes when new headerBanner is created
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, headerBanner?.id]);

    const isNew = isNaN(Number(headerBannerIdParam));

    const handleSubmit = (values: HeaderBannerFormValues) => {
        dispatch(HeaderBannerDetailActions.saveHeaderBannerDetail(values));
    };

    const handleDelete = () => {
        if (headerBannerIdParam && !isNaN(headerBannerIdParam)) {
            dispatch(HeaderBannerActions.deleteHeaderBanner(+headerBannerIdParam));
        }
    };

    const handleDrop =
        (form: FormikProps<HeaderBannerFormValues>, name: string) =>
        async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            if (rejectedFiles.length > 0) {
                if (rejectedFiles[0].errors[0].code === ErrorCode.FileInvalidType) {
                    form.setFieldError(name, 'app.error.invalidImageType');
                } else if (rejectedFiles[0].errors[0].code === ErrorCode.FileTooLarge) {
                    form.setFieldError(name, 'app.error.maxFiveMb');
                } else {
                    form.setFieldError(name, 'app.error.maxOneFile');
                }

                form.setFieldTouched(name, true, false);

                return;
            }

            if (!acceptedFiles[0]?.type || !validImageMimeTypes.includes(acceptedFiles[0].type)) {
                form.setFieldError(name, 'app.error.invalidImageType');
                form.setFieldTouched(name, true, false);
                return;
            }

            const image = { name: acceptedFiles[0].name, contentBase64: await base64encode(acceptedFiles[0]) };

            if (name === 'image') {
                setNewImageFile(image);
            } else if (name === 'mobileImage') {
                setNewMobileImageFile(image);
            }

            form.setFieldValue(name, image);
        };

    const handleValueChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        max: number,
        form: FormikProps<HeaderBannerFormValues>,
        field: FieldInputProps<string>
    ) => form.setFieldValue(field.name, e.target.value.slice(0, max));

    return (
        <PoolSpinner enabled={requesting} sx={{ width: '100%', height: '100%' }}>
            <Formik
                initialValues={headerBannerFormValues}
                onSubmit={handleSubmit}
                validationSchema={getValidationSchema(selectedLanguage)}
                enableReinitialize={true}
            >
                <PaloContentDetailShell
                    title="app.contentManagement.headerBanner.detailHeader"
                    isNew={isNew}
                    onDelete={handleDelete}
                    listRoute={ROUTES.contentManagement.headerBanner.list}
                    deleteTitle={<FormattedMessage id="app.contentManagement.headerBanner.delete" />}
                    deleteWarning={<HeaderBannerDeleteWarning />}
                >
                    <ScrollToFieldError />
                    <Box m={theme => theme.spacing(2, theme.general.pageContentSpacing)}>
                        <PaloFormSection title={<FormattedMessage id="app.contentManagement.picture" />}>
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={9.5}>
                                    <Field name="image">
                                        {({ field, form, meta }: FieldProps<Base64File>) => (
                                            <>
                                                <PictureDropzone
                                                    onDrop={handleDrop(form, field.name)}
                                                    error={!!meta.error && !!meta.touched}
                                                    maxSize={MAX_FILE_SIZE}
                                                    path={newImageFile?.contentBase64 ?? field.value?.pathname}
                                                    imageRatio={ImageRatio.TwentyFourByFive}
                                                    alt={field.value?.name}
                                                    multiple={false}
                                                    disabled={selectedLanguage !== currentLocale}
                                                >
                                                    <PoolTypography
                                                        typography="body2"
                                                        component="span"
                                                        color="grey.700"
                                                        id="app.contentManagement.picture.desktop"
                                                        values={{ validImageTypes: validImageTypes.join(', ') }}
                                                    />
                                                    <PoolTypography
                                                        typography="body2"
                                                        component="span"
                                                        color="grey.700"
                                                        id="app.contentManagement.picture.constraint"
                                                        values={{ resolution: '1920x400px' }}
                                                    />
                                                </PictureDropzone>
                                                {!!meta.error && !!meta.touched && (
                                                    <FormHelperText error={true}>
                                                        <FormattedMessage
                                                            id={meta.error}
                                                            values={{ validImageTypes: validImageTypes.join(', ') }}
                                                        />
                                                    </FormHelperText>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={2.5}>
                                    <Field name="mobileImage">
                                        {({ field, form, meta }: FieldProps<Base64File>) => (
                                            <>
                                                <PictureDropzone
                                                    onDrop={handleDrop(form, field.name)}
                                                    error={!!meta.error && !!meta.touched}
                                                    path={newMobileImageFile?.contentBase64 ?? field.value?.pathname}
                                                    imageRatio={ImageRatio.Square}
                                                    maxSize={MAX_FILE_SIZE}
                                                    alt={field.value?.name}
                                                    multiple={false}
                                                    disabled={selectedLanguage !== currentLocale}
                                                >
                                                    <PoolTypography
                                                        typography="body2"
                                                        component="span"
                                                        color="grey.700"
                                                        id="app.contentManagement.picture.mobile"
                                                        values={{ validImageTypes: validImageTypes.join(', ') }}
                                                    />
                                                    <PoolTypography
                                                        typography="body2"
                                                        component="span"
                                                        color="grey.700"
                                                        id="app.contentManagement.picture.constraint.resolution"
                                                        values={{ resolution: '375x375px' }}
                                                    />
                                                    <PoolTypography
                                                        typography="body2"
                                                        component="span"
                                                        color="grey.700"
                                                        id="app.contentManagement.picture.constraint.size"
                                                    />
                                                </PictureDropzone>
                                                {!!meta.error && !!meta.touched && (
                                                    <FormHelperText error={true}>
                                                        <FormattedMessage
                                                            id={meta.error}
                                                            values={{ validImageTypes: validImageTypes.join(', ') }}
                                                        />
                                                    </FormHelperText>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                        </PaloFormSection>

                        <PaloFormSection
                            title={<FormattedMessage id="app.contentManagement.headerBanner.displayTime" />}
                        >
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={6}>
                                    <FormikPoolDatePicker
                                        name="validFrom"
                                        label={
                                            <FormattedMessage id="app.contentManagement.headerBanner.displayTimeValidFrom" />
                                        }
                                        readOnly={selectedLanguage !== currentLocale}
                                    />
                                </Grid>
                                <Grid item={true} xs={6}>
                                    <FormikPoolDatePicker
                                        name="validTo"
                                        label={
                                            <FormattedMessage id="app.contentManagement.headerBanner.displayTimeValidTo" />
                                        }
                                        readOnly={selectedLanguage !== currentLocale}
                                    />
                                </Grid>
                            </Grid>
                        </PaloFormSection>

                        <PaloFormSection
                            title={
                                <OptionalSectionTitle>
                                    <FormattedMessage id="app.label.text" />
                                </OptionalSectionTitle>
                            }
                        >
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={6}>
                                    <Field name={`headline.${selectedLanguage}`}>
                                        {({ field, form, meta }: FieldProps<string>) => (
                                            <PoolTextField
                                                {...field}
                                                fullWidth={true}
                                                label={
                                                    <FormattedMessage id="app.contentManagement.headerBanner.title" />
                                                }
                                                value={field.value ?? ''}
                                                onChange={e => handleValueChange(e, 35, form, field)}
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : (
                                                        <FormattedMessage
                                                            id="app.contentManagement.headerBanner.titleCharacterCount"
                                                            values={{ value: field.value?.length }}
                                                        />
                                                    )
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Field name={`subHeadline.${selectedLanguage}`}>
                                        {({ field, form, meta }: FieldProps<string>) => (
                                            <PoolTextField
                                                {...field}
                                                value={field.value ?? ''}
                                                fullWidth={true}
                                                label={
                                                    <FormattedMessage id="app.contentManagement.headerBanner.subTitle" />
                                                }
                                                error={!!meta.touched && !!meta.error}
                                                onChange={e => handleValueChange(e, 95, form, field)}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : (
                                                        <FormattedMessage
                                                            id="app.contentManagement.headerBanner.subTitleCharacterCount"
                                                            values={{ value: field.value?.length }}
                                                        />
                                                    )
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                        </PaloFormSection>

                        <PaloFormSection
                            title={
                                <OptionalSectionTitle>
                                    <FormattedMessage id="app.contentManagement.headerBanner.button" />
                                </OptionalSectionTitle>
                            }
                        >
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={6}>
                                    <Field name={`buttonText.${selectedLanguage}`}>
                                        {({ field, meta }: FieldProps<string>) => (
                                            <PoolTextField
                                                {...field}
                                                fullWidth={true}
                                                value={field.value ?? ''}
                                                label={<FormattedMessage id="app.label.text" />}
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Field name={`buttonUrl.${selectedLanguage}`}>
                                        {({ field, meta }: FieldProps<string>) => (
                                            <PoolTextField
                                                {...field}
                                                fullWidth={true}
                                                value={field.value ?? ''}
                                                label={
                                                    <FormattedMessage id="app.contentManagement.headerBanner.link" />
                                                }
                                                placeholder={intl.formatMessage({
                                                    id: 'app.common.exampleLink',
                                                })}
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                        </PaloFormSection>
                    </Box>
                </PaloContentDetailShell>
            </Formik>
        </PoolSpinner>
    );
};

/** The warning names the entry, which only the form context knows. */
const HeaderBannerDeleteWarning = (): ReactElement => {
    const { supportedLocales } = useAppSelector(state => state);
    const { values } = useFormikContext<HeaderBannerFormValues>();

    return (
        <PoolFormattedMessage
            id="app.contentManagement.headerBanner.deleteWarning"
            values={{ name: getLocalizedString(supportedLocales, values.headline) }}
        />
    );
};
